import { initDatadog } from '../datadog/init';

/**
 * Configuration object for Datadog RUM initialization from Rails.
 */
export interface DatadogRailsConfig {
  applicationId: string;
  clientToken: string;
  service: string;
  env: string;
  version: string;
  sessionSampleRate: number;
  userId?: string;
  organizationId?: string;
  isImpersonatedUser?: boolean;
}

/**
 * Extended DD_RUM interface for Rails usage.
 * Includes the onReady queue mechanism used by the Datadog CDN script.
 */
interface DD_RUM_Rails {
  q: Array<() => void>;
  onReady: (callback: () => void) => void;
  init: (config: any) => void;
  setTrackingConsent: (consent: 'granted' | 'not-granted') => void;
  setUser: (user: {
    id?: string;
    organization_id?: string;
    is_impersonated_user?: boolean;
  }) => void;
  addAction: (name: string, context?: Record<string, unknown>) => void;
  getInitConfiguration?: () => any;
}

/**
 * Load the Datadog RUM script dynamically and set up the onReady queue.
 * This creates a stub DD_RUM object with an onReady queue that will be
 * replaced when the actual Datadog script loads.
 */
export function loadDatadogScript(): void {
  // Initialize DD_RUM stub with onReady queue if not already present
  if (!(window as any).DD_RUM) {
    (window as any).DD_RUM = {
      q: [] as Array<() => void>,
      onReady: function (callback: () => void) {
        this.q.push(callback);
      },
    };
  }

  // Create and inject the Datadog RUM script
  const script = document.createElement('script');
  script.async = true;
  script.src = 'https://www.datadoghq-browser-agent.com/us1/v6/datadog-rum.js';

  // Insert before the first script tag, or append to head if none exist
  const firstScript = document.getElementsByTagName('script')[0];
  if (firstScript && firstScript.parentNode) {
    firstScript.parentNode.insertBefore(script, firstScript);
  } else {
    document.head.appendChild(script);
  }
}

/**
 * Initialize Datadog RUM with the provided configuration.
 * This should be called within the DD_RUM.onReady callback.
 *
 * @param config - Configuration object for Datadog RUM
 */
export function initializeDatadog(config: DatadogRailsConfig): void {
  const DD_RUM = (window as any).DD_RUM as DD_RUM_Rails | undefined;

  if (!DD_RUM) {
    console.error('DD_RUM is not available on window');
    return;
  }

  // Use shared initialization which handles:
  // - Building the configuration
  // - Calling DD_RUM.init()
  // - Setting up consent change listeners for cookie consent
  initDatadog(
    DD_RUM,
    {
      applicationId: config.applicationId,
      clientToken: config.clientToken,
      service: config.service,
      env: config.env,
      version: config.version,
      sessionSampleRate: config.sessionSampleRate,
    },
    {
      // Rails pages don't use an SPA router, so views are tracked automatically
      trackViewsManually: false,
    }
  );

  // Set user context if provided (Rails-specific)
  if (
    config.userId ||
    config.organizationId ||
    config.isImpersonatedUser !== undefined
  ) {
    DD_RUM.setUser({
      id: config.userId,
      organization_id: config.organizationId,
      is_impersonated_user: config.isImpersonatedUser,
    });
  }

  DD_RUM.addAction('datadog initialized');
}

/**
 * Main entry point for Datadog initialization from Rails.
 * Loads the Datadog script and initializes it with the provided config.
 *
 * @param config - Configuration object for Datadog RUM
 */
export function initDatadogRails(config: DatadogRailsConfig): void {
  loadDatadogScript();

  const DD_RUM = (window as any).DD_RUM;
  DD_RUM.onReady(() => {
    initializeDatadog(config);
  });
}
